import clsx from 'clsx';
import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { withTimeout } from 'utils/promiseUtils/withTimeout';

import s from './MicroFrontend.module.less';
import { MicroFrontendName, MicroFrontendProps, RenderFn, TManifest, UnmountFn } from './MicroFrontend.types';
import { addQuery, DEFAULT_LOAD_TIMEOUT_MS, removeById, safeReloadOnce } from './MicroFrontend.utils';

const getWindowValue = (key: string): unknown => (window as unknown as Record<string, unknown>)[key];

const getRenderFn = (name: MicroFrontendName): RenderFn | null => {
  const value = getWindowValue(`render${name}`);

  return typeof value === 'function' ? (value as RenderFn) : null;
};

const getUnmountFn = (name: MicroFrontendName): UnmountFn | null => {
  const value = getWindowValue(`unmount${name}`);

  return typeof value === 'function' ? (value as UnmountFn) : null;
};

export const MicroFrontend: FC<MicroFrontendProps> = ({ name, containerId, host, appParams, onRender }) => {
  const hasLoadedRef = useRef(false);

  const isMobile = Boolean((appParams as { isMobile?: boolean })?.isMobile);

  const renderMicroFrontend = useCallback(() => {
    const renderFn = getRenderFn(name);

    if (!renderFn) {
      throw new Error(`window.render${name} is not a function`);
    }

    renderFn(containerId, appParams);
    onRender?.();
  }, [appParams, containerId, name, onRender]);

  const unmountMicroFrontend = useCallback(() => {
    const unmountFn = getUnmountFn(name);
    unmountFn?.(containerId);
  }, [containerId, name]);

  /**
   * Loads the micro-frontend assets (CSS + JS) and initializes it.
   *
   * Recovery strategy:
   * - First failure → retry loading once
   * - Second failure → perform a guarded full page reload
   */
  const loadMicroFrontend = useMemo(
    () =>
      async (scriptId: string, attempt: number): Promise<void> => {
        const existingRenderFn = getRenderFn(name);

        if (existingRenderFn) {
          renderMicroFrontend();

          return;
        }

        const tsParam = `ts=${Date.now()}`;

        removeById(scriptId);
        removeById(`${scriptId}-css`);

        const response = await withTimeout(
          () => fetch(`${host}/asset-manifest.json?${tsParam}`, { cache: 'no-store' }),
          {
            timeout: DEFAULT_LOAD_TIMEOUT_MS,
            label: 'asset-manifest fetch',
          },
        );

        if (!response.ok) {
          throw new Error(`asset-manifest fetch failed: ${response.status}`);
        }

        const manifest = (await response.json()) as TManifest;

        const jsRaw = manifest.files['main.js'];

        if (!jsRaw) {
          throw new Error('main.js is missing in asset-manifest');
        }

        const cssRaw = manifest.files['main.css'];

        const retryLoad = async (scriptId: string) => {
          try {
            await loadMicroFrontend(scriptId, 1);
          } catch (retryError) {
            safeReloadOnce(retryError);
          }
        };

        /**
         * Decides whether to retry loading or perform a full reload.
         * The reload is guarded to happen only once per browser tab.
         */
        const retryOrReload = (error: unknown) => {
          if (attempt === 0) {
            void retryLoad(scriptId);

            return;
          }

          safeReloadOnce(error);
        };

        const attachScript = () => {
          const script = document.createElement('script');
          script.id = scriptId;
          script.crossOrigin = '';
          script.type = 'module';

          const jsUrl = jsRaw.startsWith('http') ? jsRaw : `${host}${jsRaw}`;
          // No cache-busting query: the build is hashed, and a URL with a query is a different module than the
          // same file imported by its own chunks, so the messenger would run twice with an uninitialised i18n.
          script.src = jsUrl;

          script.onload = () => {
            try {
              renderMicroFrontend();
            } catch (error) {
              retryOrReload(error);
            }
          };

          script.onerror = (error) => {
            retryOrReload(error);
          };

          document.head.appendChild(script);
        };

        if (cssRaw) {
          const link = document.createElement('link');
          link.id = `${scriptId}-css`;
          link.type = 'text/css';
          link.rel = 'stylesheet';

          const cssUrl = cssRaw.startsWith('http') ? cssRaw : `${host}${cssRaw}`;
          link.href = addQuery(cssUrl, tsParam);

          link.onload = () => {
            attachScript();
          };

          link.onerror = (error) => {
            retryOrReload(error);
          };

          document.head.appendChild(link);
        } else {
          attachScript();
        }
      },
    [host, name, renderMicroFrontend],
  );

  useEffect(() => {
    return () => {
      unmountMicroFrontend();
    };
  }, [unmountMicroFrontend]);

  useEffect(() => {
    const scriptId = `micro-frontend-script-${name}`;

    if (getRenderFn(name)) {
      try {
        renderMicroFrontend();
      } catch (error: unknown) {
        console.error(`Rendering of ${name} application has failed.\n`, error);
        safeReloadOnce(error);
      }

      hasLoadedRef.current = true;

      return;
    }

    if (hasLoadedRef.current) return;

    void loadMicroFrontend(scriptId, 0)
      .then(() => {
        hasLoadedRef.current = true;
      })
      .catch((error: unknown) => {
        console.error(`Downloading of ${name} application has failed.\n`, error);
        safeReloadOnce(error);
      });
  }, [loadMicroFrontend, name, renderMicroFrontend]);

  return (
    <div
      id={containerId}
      className={clsx(s.microFrontendContainer, {
        [s.isMobile]: isMobile,
      })}
    />
  );
};
